"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useUser } from '../../context/UserContext';
import DiscountButton from '../ui/DiscountButton';
import { useComingSoon } from '../../context/ComingSoonContext';

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  // Use the user context instead of local auth state
  const { 
    user, 
    isAuthenticated, 
    login, 
    signup, 
    logout 
  } = useUser();
  
  const { openPopup } = useComingSoon();
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  
  // Close sidebar drawer on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileOpen) {
        setMobileOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileOpen, setMobileOpen]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
    
    const success = await login(email, password);
    if (success) {
      setShowAuthModal(false);
    }
  };
  
  const handleSignup = async (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const email = e.target.email.value;
    const password = e.target.password.value;
    
    const success = await signup(name, email, password);
    if (success) {
      setShowAuthModal(false);
    }
  };
  
  const handleLogout = () => {
    logout();
    setShowProfileMenu(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      // Show error
      return;
    }
    
    try {
      // In a real app, you would call an API endpoint
      // await fetch('/api/forgot-password', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email: forgotPasswordEmail })
      // });
      
      // Mock successful password reset request
      alert('Password reset link sent to your email!');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      alert('Failed to send password reset link. Please try again.');
    }
  };

  return (
    <>
      {/* Mobile sidebar overlay - only visible when sidebar is open on mobile */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <aside 
        className={`${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 fixed md:sticky top-0 left-0 bottom-0 z-50 w-72 bg-white border-r border-gray-100 shadow-sm transition-transform duration-300 ease-in-out flex flex-col h-screen`}
      >
        {/* Logo and close button section */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <Link href="/" className="flex items-center">
            <img 
              src="/images/logo-oval.svg" 
              alt="OpenMenu.pk Logo"
              className="w-8 h-8 mr-3"
            />
            <h1 className="text-lg font-bold text-primary">OpenMenu.pk</h1>
          </Link>
          
          {/* Mobile close button - only visible on mobile when sidebar is open */}
          <button 
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Rest of the sidebar content */}
        <div className="flex flex-col h-full">
          {/* Top section with scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Get Launch Discount Button */}
            <div className="px-4 pt-5 pb-2">
              <DiscountButton onClick={openPopup} />
            </div>
            
            {/* New Search Button */}
            <div className="px-4 pt-2 pb-3">
              <Link 
                href="/explore" 
                className="w-full flex items-center justify-center px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 transition-colors border border-gray-100"
                onClick={() => mobileOpen && setMobileOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="font-medium">Explore Restaurants</span>
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="px-4 py-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pl-3 mb-2">Navigation</h3>
              <ul className="space-y-1">
                <li>
                  <Link 
                    href="/" 
                    className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 hover:text-primary transition-colors group"
                    onClick={() => mobileOpen && setMobileOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5 text-gray-400 group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l9-9 9 9m-9 3v7a2 2 0 002 2h2a2 2 0 002-2v-3a2 2 0 002-2h2" />
                    </svg>
                    <span className="font-medium">Home</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Recent Views */}
            <div className="px-4 py-2 mt-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pl-3 mb-2">Recent Views</h3>
              <ul className="space-y-1">
                <li>
                  <Link 
                    href="/restaurant/burger-king" 
                    className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                    onClick={() => mobileOpen && setMobileOpen(false)}
                  >
                    <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center mr-2.5 text-white">
                      🍔
                    </div>
                    <span className="font-medium">Burger King</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/restaurant/pizza-hut" 
                    className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                    onClick={() => mobileOpen && setMobileOpen(false)}
                  >
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center mr-2.5 text-white">
                      🍕
                    </div>
                    <span className="font-medium">Pizza Hut</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/restaurant/kfc" 
                    className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                    onClick={() => mobileOpen && setMobileOpen(false)}
                  >
                    <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center mr-2.5 text-white">
                      🍗
                    </div>
                    <span className="font-medium">KFC</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Saved Restaurants */}
            {isAuthenticated && (
              <div className="px-4 py-2 mt-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pl-3 mb-2">Saved Restaurants</h3>
                <ul className="space-y-1">
                  <li>
                    <Link 
                      href="/restaurant/mcdonalds" 
                      className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                      onClick={() => mobileOpen && setMobileOpen(false)}
                    >
                      <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center mr-2.5 text-white">
                        🍟
                      </div>
                      <span className="font-medium">McDonald's</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      href="/restaurant/subway" 
                      className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                      onClick={() => mobileOpen && setMobileOpen(false)}
                    >
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-2.5 text-white">
                        🥖
                      </div>
                      <span className="font-medium">Subway</span>
                    </Link>
                  </li>
                </ul>
              </div>
            )}
            
            {/* My Orders */}
            {isAuthenticated && (
              <div className="px-4 py-2 mt-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pl-3 mb-2">My Orders</h3>
                <ul className="space-y-1">
                  <li>
                    <Link 
                      href="/orders" 
                      className="flex items-center py-2 px-3 rounded-lg hover:bg-gray-50 text-gray-700 hover:text-primary transition-colors group"
                      onClick={() => mobileOpen && setMobileOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5 text-gray-400 group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <span className="font-medium">View All Orders</span>
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>
          
          {/* User Profile - Sticky section */}
          <div className="border-t border-gray-100 bg-white">
            {isAuthenticated ? (
              <div className="relative">
                <div 
                  className="p-4 flex items-center cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                >
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl mr-3">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.credits || 0} Credits</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {/* Profile dropdown menu */}
                {showProfileMenu && (
                  <div className="absolute bottom-full w-full bg-white border-t border-gray-100 shadow-lg rounded-t-lg overflow-hidden">
                    <ul>
                      <li>
                        <Link 
                          href="/account/profile" 
                          className="w-full text-left py-3 px-4 hover:bg-gray-50 flex items-center"
                          onClick={() => {
                            setShowProfileMenu(false);
                            mobileOpen && setMobileOpen(false);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          My Profile
                        </Link>
                      </li>
                      <li>
                        <Link 
                          href="/account/addresses" 
                          className="w-full text-left py-3 px-4 hover:bg-gray-50 flex items-center"
                          onClick={() => {
                            setShowProfileMenu(false);
                            mobileOpen && setMobileOpen(false);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          My Addresses
                        </Link>
                      </li>
                      <li>
                        <Link 
                          href="/orders" 
                          className="w-full text-left py-3 px-4 hover:bg-gray-50 flex items-center"
                          onClick={() => {
                            setShowProfileMenu(false);
                            mobileOpen && setMobileOpen(false);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          My Orders
                        </Link>
                      </li>
                      <li>
                        <button 
                          className="w-full text-left py-3 px-4 hover:bg-gray-50 flex items-center text-red-600"
                          onClick={handleLogout}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 flex items-center justify-between">
                <p className="text-gray-700 font-medium">Not signed in</p>
                <button 
                  onClick={() => {
                    setAuthMode('login');
                    setShowAuthModal(true);
                  }}
                  className="px-4 py-2 bg-primary text-white font-medium hover:bg-opacity-90 rounded-lg transition-colors"
                >
                  Sign in
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      
      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-96 p-6 animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                {showForgotPassword ? 'Reset Password' : (authMode === 'login' ? 'Login to your account' : 'Create an account')}
              </h2>
              <button 
                onClick={() => {
                  setShowAuthModal(false);
                  setShowForgotPassword(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {showForgotPassword ? (
              <form onSubmit={handleForgotPassword}>
                <div className="mb-4">
                  <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    id="forgotEmail" 
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                    placeholder="your@email.com"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>
                <button 
                  type="submit"
                  className="w-full py-2 bg-primary text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Send Reset Link
                </button>
                <p className="text-center mt-4 text-sm text-gray-600">
                  <button 
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="text-primary font-medium hover:underline"
                  >
                    Back to Login
                  </button>
                </p>
              </form>
            ) : authMode === 'login' ? (
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input 
                      type={showPassword ? "text" : "password"}
                      id="password" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        )}
                        {!showPassword && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button 
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
                <button 
                  type="submit"
                  className="w-full py-2 bg-primary text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Login
                </button>
                <p className="text-center mt-4 text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button 
                    type="button"
                    onClick={() => setAuthMode('signup')}
                    className="text-primary font-medium hover:underline"
                  >
                    Sign up
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSignup}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input 
                      type={showSignupPassword ? "text" : "password"}
                      id="password" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showSignupPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        )}
                        {!showSignupPassword && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
                <button 
                  type="submit"
                  className="w-full py-2 bg-primary text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Create Account
                </button>
                <p className="text-center mt-4 text-sm text-gray-600">
                  Already have an account?{' '}
                  <button 
                    type="button"
                    onClick={() => setAuthMode('login')}
                    className="text-primary font-medium hover:underline"
                  >
                    Login
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
} 